import { useSyncExternalStore } from 'react';

/**
 * The ☾/☀ light/dark switch, as old (hubTheme). The choice is kept per browser in localStorage and put on
 * <html data-theme>; with none, the OS decides. index.html sets it before the first paint; the CSS follows
 * the attribute through themeCss.ts.
 */
export const THEME_KEY = 'hubTheme';
export type Theme = 'light' | 'dark';

const DARK_OS = '(prefers-color-scheme: dark)';
/** Sent on window after a switch: anything that draws with theme colours in JS can repaint. */
export const THEME_EVENT = 'hubtheme';

/** The theme shown now: the choice on <html>, else the OS one. */
export function currentTheme(): Theme {
  const set = document.documentElement.dataset.theme;
  if (set === 'dark' || set === 'light') return set;
  return window.matchMedia?.(DARK_OS).matches ? 'dark' : 'light';
}

/** Switches to the other theme and remembers it in this browser. */
export function flipTheme(): Theme {
  const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
  document.documentElement.dataset.theme = next;
  try {
    localStorage.setItem(THEME_KEY, next);
  } catch {
    /* private window: this visit only */
  }
  window.dispatchEvent(new Event(THEME_EVENT));
  return next;
}

/** Calls `onChange` when the theme shown may have changed: a switch, or the OS one (when nothing is chosen). */
function subscribe(onChange: () => void) {
  const os = window.matchMedia?.(DARK_OS);
  os?.addEventListener('change', onChange);
  window.addEventListener(THEME_EVENT, onChange);
  return () => {
    os?.removeEventListener('change', onChange);
    window.removeEventListener(THEME_EVENT, onChange);
  };
}

/** The header button: the moon in light mode, the sun in dark mode. */
export function ThemeToggle() {
  const theme = useSyncExternalStore(subscribe, currentTheme, () => 'light' as Theme);
  const label = theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
  return (
    <button type="button" className="hub-th" title={label} aria-label={label} onClick={flipTheme}>
      {theme === 'dark' ? '☀' : '☾'}
    </button>
  );
}
